import * as Enumerable from "linq";
import { HtmlHelper } from "../../../../../helpers/html-helper";
import { NumberFormatHelper } from "../../../../../helpers/number-format-helper";
import { EntityTypeConfigurationModel } from "../../../../../models/basics/basics.models";
import { VetRuleRunItemScoreClientModel } from "../../../../../models/vetting/vetting.models";
import { IncidentGracePeriod } from '@rightship-tech/common-ui/models/incidents/incident.models';

export enum PortRiskProfileTabsEnum {
    PreArrivals = 1,
    InPort = 2,
    Departed = 3
}

export enum VetTaskOutcomeEnum {
    Pass = 1,
    Fail = 2,
    NA = 3
}

export class VesselPortCallODataModel {
    vesselPortCallId: number;
    vesselSKey: number;
    vesselName?: string;
    vesselIdentifier?: string;
    vesselIdentifierType?: string;
    overallScreeningRisk?: string;
    lastScreeningDate?: Date;
    formsSubmitted?: string;
    lastFormRequestedDate?: Date;
    fbrReports?: number;
    firstSubmittedFBRDate?: Date;
    portSKey?: number;
    portName?: string;
    berthSKey?: number;
    berthName?: string;
    terminalSKey?: number;
    terminalName?: string;
    eta?: Date;
    etd?: Date;
    reviewStatus?: string;
    lastStatusDate?: Date;
    numberOfStatusAttachments?: number;
    statusRemarks?: string;
}

export interface PortTerminalAndBerthResponse {

    ports: PortsTerminalBerthsResponseItem[],
    countries: PortsTerminalBerthsResponseItem[],
    terminals: PortsTerminalBerthsResponseItem[],
    berths: PortsTerminalBerthsResponseItem[]
}

export interface SaveBuPortConfigRequest {
    portConfigs: SaveBuPortConfigModel[];
    businessUnitId: number;
    departureDaysFromArrival?: number;
}

export interface GetPortCallSummaryRequest {
    portIds: string;
    countryIds: string;
    terminalIds: string;
    berthIds: string;
    etaFrom: string;
    etaTo: string;
    businessUnitId?: number;
    portCallType?: string;
}

export interface PortCallSummaryItem {
    risk: string;
    reviewStatus: string;
    vesselCount: number;
}

export class PortsTerminalBerthsResponseItem {
    id: number;
    name: string;
    parentId?: number;

    public static getIdsFromLookups(items: PortsTerminalBerthsResponseItem[], selectedItems: PortsTerminalBerthsResponseItem[]): number[] {
        return selectedItems && items && (items.length == 1 || items.length != selectedItems.length) ? Enumerable.from(selectedItems).select(x => x.id).toArray() : [];
    }

    public static getSelectedItems(items: PortsTerminalBerthsResponseItem[], itemIds: number[]): PortsTerminalBerthsResponseItem[] {
        return Enumerable.from(items).where(x => itemIds.indexOf(x.id) > -1).toArray();
    }
}

export interface GetVesselPreArrivalRequest {
    businessUnitId?: number;
    vesselPortCallId: number;
}

export interface GetVesselPreArrivalResponse {
    vesselPreArrivalDetails: VesselPreArrivalDetailsModel;
    vetRiskScores: VesselPortCallRiskScoreModel[];
}

export interface VesselPortCallRiskScoreModel {
    name: string;
    riskLevelId: string;
    riskLevel: string;
    riskCategoryId: string;
    countLow: number;
    countMedium: number;
    countHigh: number;
}

export interface VesselPreArrivalDetailsModel {
    createdDate: string;
    isVerified: boolean;
    bKey: string;
    isSanctioned: boolean;
    rsInspectionRequired: boolean;
    emissionsRating: string;
    emissionsRatingPlus: boolean;
    vesselType: string;
    vesselStatus: string;
    vesselStatusCode: string;
    fullVetId: number;
    isInGeoPoliticalZone: boolean;
    hasGeopoliticalConflict: boolean;
    dwt: number;
    maxDwt: number;
    flag: string;
    starRating: number;
    dateOfBuild: Date;
    lastRightShipInspectionId: number;
    listCount: number;
    requestedDateOfInspection: Date;
    rightShipInspectionExpiryDate: Date;
    reasonForInspection: string;
    actualDateOfInspection: Date;
    vesselId: number;
    safetyRatingType: number;
    notInService: boolean;
    rightShipInspectionStatus: string;
    requiresRightShipInspection: boolean;
    age: number;
    daysRemainingMinutely: number;
    daysRemaining: number;
    inspectionState: number;
    inspectionRequired: boolean;
    imo: string;
    eni: string;
    vin: string;
    label: string;
    identifier: string;
    vesselName: string;
    officialNumber: string;
    fbrsCount: number;
    passedFbrsCount: number;
    passedFbrsPercentage: number;
    vetId: number;
    forms: PortCallFormModel[];
    statuses: PortCallStatusHistoryItemModel[];
    portSKey: number;
    vesselSKey: number;
    vesselPortCallId: number;
    dateOfAnalysis: string;
    cargoTypeCategoryId?: number;
    estimatedArrivalTime: string;
    estimatedDepartureTime: string;
    incidentGracePeriods: IncidentGracePeriod[]; 
}

export interface PortCallFormModel {
    name: string;
    formId: number;
    status: string;
    submittedOn: string;
    validUntil: string;
    isValid: boolean;
}

export const PortsAndTerminalUrls = {
    CreateVesselPortArrival: "PortsAndTerminals/CreateVesselPortArrival",
    LoadPortsTerminalsAndBerths: "PortsAndTerminals/LoadPortsTerminalsAndBerths",
    UpdateVesselPortArrival: "PortsAndTerminals/UpdateVesselPortArrival",
    GetVesselPreArrival: "PortsAndTerminals/GetVesselPreArrival",
    GetVesselPortCallStatuses: "PortsAndTerminals/GetVesselPortCallStatuses",
    GetPortCallSummaryItems: "PortsAndTerminals/GetPortCallSummaryItems",
    SaveBuPortConfig: "PortsAndTerminals/SaveBuPortConfig",
    GetBuPortConfig: "PortsAndTerminals/GetBuPortConfig",
    LoadPortCallStatuses: "PortsAndTerminals/LoadPortCallStatuses",
    LoadScreeningRisks: "PortsAndTerminals/LoadScreeningRisks",
    GetPortForms: "PortsAndTerminals/GetPortForms",
    CreateVesselForms: "PortsAndTerminals/CreateVesselForms",
    GetValidVesselPortCallForms: "PortsAndTerminals/GetValidVesselPortCallForms",
    GetRiskHistory: "PortsAndTerminals/GetRiskHistory",
    GetRiskAnalysisRules: "PortsAndTerminals/GetRiskAnalysisRules",
    UploadVesselPortCalls: "PortsAndTerminals/UploadVesselPortCalls",
    GetVesselPortCallAssignment: "PortsAndTerminals/GetVesselPortCallAssignment",
    SetVesselPortCallAssignment: "PortsAndTerminals/SetVesselPortCallAssignment",
    ChangeVesselPortCallStatus: "PortsAndTerminals/ChangeVesselPortCallStatus",
    SubmitFbr: "PortsAndTerminals/SubmitFbr",
    GetPortOffset: "PortsAndTerminals/GetPortOffset"
};

export const PTEntityTypeConfigurationConstants: EntityTypeConfigurationModel[] = [
    {
        title: "Vessels",
        uri: "PTVessels",
        key: 'PTVesselSearch',
        typeSingular: "Vessel",
        isVisible: true,
        properties: "name",
        format: (filter, item) => {

            let html = "<div class='entity-finder-search-vessel-item column'><p class='name'>" + item.vesselName + "</p>"

            html += "<div class='prop-container row'>";
            if (item.vesselIdentifier)
                html += `<span class='prop'>${item.vesselIdentifierType} ${HtmlHelper.syntaxHighlight(filter, item.vesselIdentifier)}</span>`;

            if (item.dwt)
                html += `<span class='prop'>DWT ${NumberFormatHelper.getFormattedValue(item.dwt, null, 0, 0)}</span>`;
            if (item.vesselType)
                html += `<span class='prop'>${item.vesselType}</span>`;

            html += "</div>";
            html += "</div>";

            return html;
        },
        claims: [],
        excludeInDashboard: () => { return true; }
    }
];

export interface BuPortConfigModel {
    portBKey: number;
    isNew: boolean;
    //Forms belonging to port
    forms: BuPortFormConfigModel[];
    //Ids of forms belonging to port
    formIds: number[];
    portName?: string;
    countryName?: string;
    isDeleted?: boolean;
    isModified?: boolean;
    ruleCollectionId?: number;
}

export interface BuPortFormConfigModel {
    /** Form configuration id */
    onboardingConfigId?: number;
    /** Form config id in Database */
    formConfigMemberId?: number;
    /** Original form configuration id (when loaded from DB) */
    originalOnboardingConfigId?: number;
    /** Original validity period in days(when loaded from DB) */
    originalFormValidityPeriodInDays?: number;
    departureDaysFromArrival?: number;
    onboardingConfigName?: string;
    isModified?: boolean;
    isDeleted?: boolean;
    formValidityPeriodInDays?: number;
}

export interface SaveBuPortConfigModel {
    /** Port config id in Database */
    portConfigMemberId?: number;
    portBKey: number;
    onboardingConfigId: number;
    onboardingConfigName?: string;
    formValidityPeriodInDays?: number;
    ruleCollectionId?: number;
    isDelete: boolean;
}

export interface PortCallStatusModel {
    statusId: number;
    statusName: string;
    isSystemStatus: boolean;
}

export interface ScreeningRiskModel {
    screeningRiskId: number;
    screeningRiskName: string;
}

export interface PortFormModel {
    formSKey: number;
    formName: string;

    //used for UI
    isSelected: boolean;
}

export interface CreateVesselFormsRequest {
    vesselBKey: number;
    vesselPortCallId: number;
    formIds: number[];
    emails: { email: string; emailRecipientTypeId: number; }[];
    businessUnitId?: number;
}

export interface CreateVesselFormsResponse {

}

export interface GetRiskHistoryRequest {
    vetId: number;
    toDate: string;
}

export interface GetRiskHistoryResponse {
    dailyItems: DailyRiskHistory[];
}

export interface DailyRiskHistory {
    date: string;
    items: DailyRiskHistoryItem[];
    //set in client side
    groupedItems: {
        key: string;
        items: DailyRiskHistoryItemWithRiskLevelModel[];
    }[];

    countOraFail?: number;
    countHighFail?: number;
    countMediumFail?: number;
    countLowFail?: number;
    riskLevelCount?: number;
    riskLevel?: string;
}

export interface DailyRiskHistoryItemWithRiskLevelModel {
    rule: DailyRiskHistoryItem;
    riskCategory: string;
    riskLevel: string;
    riskLevelId: number;
    previousRiskLevel: string;
    previousRiskLevelId: number;
    hasPreviousSystemError: boolean;
    previousVetTaskOutcome: number;
}

export interface DailyRiskHistoryItem {
    vetTaskId: number;
    hasCap: boolean;
    screeningOverviewDisplayVisibilityId: number;
    vettingOverviewDisplayVisibilityId: number;
    hasPendingPrefetchData: boolean;
    prefetchRuleActionId: number;
    hasSystemError: boolean;
    vettingRuleRunItemId: number;
    date: number;
    isActive: boolean;
    vetTaskOutcome: number;
    hasListItems: boolean;
    hasRsListItems: boolean;
    title: string;
    description: string;
    canViewDisplayResultsInOverview: boolean;
    scores: VetRuleRunItemScoreClientModel[];
}

export interface GetRiskAnalysisRulesRequest {
    vetId: number;
    riskCategoryId: number;
}

export interface GetRiskAnalysisRulesResponse {
    rules: PreArrivalRiskAnalysisRuleModel[];
}

export interface GetVesselPortCallAssignmentRequest {
    vesselPortCallId: number;
}

export interface GetVesselPortCallAssignmentResponse {
    assignedVesselPortCall: AssignedVesselPortCallModel;
}

export interface SetVesselPreArrivalAssignmentRequest {
    vesselPortCallId: number;
    assignedTo: number;
    assignmentComment: string;
}

export interface SetVesselPreArrivalAssignmentResponse {

}

export interface AssignedVesselPortCallModel {
    assignedTo: number;
    assignedToUserName?: string;
    previouslyAssignedTo?: number;
    previouslyAssignedToUserName?: string;
    assignedDate?: string;
    assignmentComment?: string;
    id?: number;
}

export interface PreArrivalRiskAnalysisRuleModel {
    name: string;
    description: string;
    riskLevel: string;
    riskLevelId: number;
    ruleActionId?: number;
    vetTaskOutcomeId?: number;
    vetTaskId: number;
    hasCap: boolean;
    isCritical: boolean;
    hasRsListItems: boolean;
    hasListItems: boolean;
    canViewDisplayResultsInOverview: boolean;
    isPass: boolean;
}

export enum VesselPreArrivalSteps {
    Summary = 1,
    Fbr = 2,
    Lists = 3,
    RiskHistory = 4
}

export interface UploadVesselPortCallsRequest {
    file: File;
    portSKey: string;
    businessUnitId?: number;
}

export enum PortCallStatusEnum {
    NotApplicable = -1,
    PendingReview = 1,
    InProgress = 2,
    Reviewed = 3,
    ReviewedWithWarnings = 4,
    Reassignment = 5
}

export enum VetRunRiskLevelsEnum {
    Low = 1,
    Medium = 2,
    High = 3,
    Ora = 4
}

export interface PortCallStatusHistoryItemModel {
    name: string;
    statusId: number;
    comment?: string;
    user: string;
    userId: number;
    date: Date;
    previousStatusId?: number;
}
